import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {IconX} from '@tabler/icons-react';
import {maskIntervalInput, normalizeIntervalDisplay} from '../utils/timestampUtils';

/**
 * A duration (`interval`) input: the HH:MM text field as before, plus a
 * hours / minutes list under it like the time picker has - except a duration
 * starts at 00:00, never at "now", and the field keeps taking anything typed
 * (25:00 is a fine duration). The value stays the HH:MM string the form
 * already converts to seconds.
 */
interface DurationPickerProps {
    value: string | null | undefined;
    onChange: (value: string) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    className?: string;
    style?: React.CSSProperties;
    disabled?: boolean;
    required?: boolean;
    isInvalid?: boolean;
    isClearable?: boolean;
    autoFocus?: boolean;
    placeholder?: string;
}

const HOURS = Array.from({length: 24}, (_, i) => i);
const MINUTES = Array.from({length: 12}, (_, i) => i * 5);

const parts = (value: string | null | undefined): {hours: number; minutes: number} => {
    const normalized = normalizeIntervalDisplay(value);
    if (!normalized) return {hours: 0, minutes: 0};
    const [h, m] = normalized.split(':');
    return {hours: parseInt(h, 10) || 0, minutes: parseInt(m, 10) || 0};
};

const pad = (n: number) => String(n).padStart(2, '0');

export const DurationPicker: React.FC<DurationPickerProps> = ({
    value,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    className = 'form-control',
    style,
    disabled = false,
    required = false,
    isInvalid = false,
    isClearable = true,
    autoFocus = false,
    placeholder = 'HH:MM',
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [rect, setRect] = useState<{top: number; left: number} | null>(null);

    const current = parts(value);
    const hasValue = !!value && String(value).trim() !== '';

    const place = () => {
        const r = inputRef.current?.getBoundingClientRect();
        if (r) setRect({top: r.bottom + 4, left: r.left});
    };

    const openPopover = () => {
        if (disabled) return;
        place();
        setOpen(true);
    };

    useEffect(() => {
        if (!open) return;
        const onDocMouseDown = (e: MouseEvent) => {
            const target = e.target as Node;
            if (popoverRef.current?.contains(target) || inputRef.current?.contains(target)) return;
            setOpen(false);
        };
        // The page scrolling moves the field, so the list follows it; a scroll
        // inside the list itself is just the user picking.
        const onScroll = (e: Event) => {
            if (popoverRef.current?.contains(e.target as Node)) return;
            place();
        };
        document.addEventListener('mousedown', onDocMouseDown);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', place);
        return () => {
            document.removeEventListener('mousedown', onDocMouseDown);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', place);
        };
    }, [open]);

    // Bring the highlighted hour/minute into the middle of its own list - only
    // the list scrolls, never the page.
    useEffect(() => {
        if (!open) return;
        popoverRef.current?.querySelectorAll<HTMLElement>('.duration-picker__list').forEach(list => {
            const item = list.querySelector<HTMLElement>('.duration-picker__item--selected');
            if (item) list.scrollTop = item.offsetTop - list.clientHeight / 2 + item.clientHeight / 2;
        });
    }, [open]);

    const pick = (hours: number, minutes: number) => {
        onChange(`${pad(hours)}:${pad(minutes)}`);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape' && open) {
            e.stopPropagation();
            setOpen(false);
            return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
            setOpen(false);
        }
        onKeyDown?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        // A click in the list keeps the focus (mousedown is prevented), so a blur
        // means the user really left the field: close the list and normalize
        // what was typed.
        setOpen(false);
        onChange(normalizeIntervalDisplay(e.target.value));
        onBlur?.(e);
    };

    const popover = open && rect ? createPortal(
        <div
            ref={popoverRef}
            className="duration-picker__popover"
            style={{top: rect.top, left: rect.left}}
            // Keep the input focused while picking, so nothing commits half-way.
            onMouseDown={(e) => e.preventDefault()}
            role="listbox"
            aria-label="Trvanie"
        >
            <div className="duration-picker__column">
                <div className="duration-picker__header">hod.</div>
                <div className="duration-picker__list">
                    {HOURS.map(h => (
                        <div
                            key={h}
                            className={`duration-picker__item${hasValue && current.hours === h ? ' duration-picker__item--selected' : ''}${!hasValue && h === 0 ? ' duration-picker__item--selected' : ''}`}
                            onClick={() => pick(h, current.minutes)}
                        >
                            {pad(h)}
                        </div>
                    ))}
                </div>
            </div>
            <div className="duration-picker__column">
                <div className="duration-picker__header">min.</div>
                <div className="duration-picker__list">
                    {MINUTES.map(m => (
                        <div
                            key={m}
                            className={`duration-picker__item${hasValue && current.minutes === m ? ' duration-picker__item--selected' : ''}${!hasValue && m === 0 ? ' duration-picker__item--selected' : ''}`}
                            onClick={() => pick(current.hours, m)}
                        >
                            {pad(m)}
                        </div>
                    ))}
                </div>
            </div>
        </div>,
        document.body,
    ) : null;

    return (
        <div className="duration-picker" style={style}>
            <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                className={`${className}${isInvalid ? ' is-invalid' : ''}`}
                placeholder={placeholder}
                value={value ?? ''}
                onChange={(e) => onChange(maskIntervalInput(e.target.value))}
                onFocus={(e) => { openPopover(); onFocus?.(e); }}
                onClick={openPopover}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                required={required}
                disabled={disabled}
                autoFocus={autoFocus}
                autoComplete="off"
            />
            {isClearable && hasValue && !disabled && (
                <button
                    type="button"
                    className="duration-picker__clear"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { onChange(''); setOpen(false); }}
                    aria-label="Vymazať"
                    title="Vymazať"
                >
                    <IconX size={14}/>
                </button>
            )}
            {popover}
        </div>
    );
};
